import { useState, useEffect, useRef } from "react";
import { Search, X, Plus } from "lucide-react";

import CountryFlag from "../../transfer-windows/CountryFlag";
import { taskboard } from "../../../services/taskboardService";

// Real club picker (product rule 5). Searches real clubs (WYSCOUT teams
// via the facade -> WSTeamSearchView) showing logo + name + country,
// INCLUDING clubs not registered on the platform. "No club" is valid;
// a clearly secondary "Add unlisted club (name only, no logo)" fallback
// is the last row of the same results. The chosen club is stored as a
// snapshot on the entry so the card/modal render a logo chip.
//
// value shape:
//   null                                             -> No club
//   { wyscout_team_id, name, country, logo }         -> real club
//   { name, unlisted: true }                         -> unlisted, name only
function monogram(name) {
  return (name || "?").trim().charAt(0).toUpperCase() || "?";
}

const ClubPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      const res = await taskboard.searchTeams(q, { signal: ctrl.signal });
      if (res.canceled) return;
      setResults(
        (res.data || []).map((t2) => ({
          wyscout_team_id: t2.wyscout_team_id ?? t2.id,
          name: t2.name,
          country: t2.country || "",
          logo: t2.image_url || t2.logo || null,
        })),
      );
      setLoading(false);
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, open]);

  const pick = (club) => {
    onChange(club);
    setOpen(false);
    setQuery("");
  };

  // Selected chip (collapsed state)
  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#E4E9F0] rounded-lg px-2.5 py-1.5 text-[13px] text-[#344054]">
          {value.logo ? (
            <img src={value.logo} alt="" className="w-5 h-5 rounded-full object-contain bg-white" referrerPolicy="no-referrer" />
          ) : (
            <span className="w-5 h-5 rounded-full bg-[#EAF2FF] text-[#1866CC] text-[9px] font-bold flex items-center justify-center">{monogram(value.name)}</span>
          )}
          <span className="font-medium">{value.name}</span>
          {value.country && <CountryFlag country={value.country} width={14} height={10} />}
          {value.unlisted && <span className="text-[10px] text-[#98A2B3] uppercase">unlisted</span>}
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-[12px] text-[#667085] hover:text-[#B91C1C]">
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 border border-[#E9E9E9] rounded-lg px-3 py-2 focus-within:ring-1 focus-within:ring-[#2283FF]">
        <Search className="w-4 h-4 text-[#98A2B3] flex-none" />
        <input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder="Search real clubs…"
          className="w-full text-[13px] outline-none placeholder:text-[#98A2B3]"
        />
        {query && (
          <button type="button" onClick={() => setQuery("")} className="text-[#98A2B3] hover:text-[#475467]">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 bg-white border border-[#E4E9F0] rounded-lg shadow-lg py-1 max-h-64 overflow-auto">
          {/* No club — always available */}
          <button type="button" onClick={() => pick(null)} className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-[#344054] hover:bg-[#F8FAFC]">
            <span className="w-6 h-6 rounded-full bg-[#F2F4F7] text-[#98A2B3] flex items-center justify-center flex-none"><X className="w-3.5 h-3.5" /></span>
            No club
          </button>

          {query.trim().length < 2 && (
            <p className="px-3 py-2 text-[12px] text-[#98A2B3]">Type at least 2 characters to search clubs.</p>
          )}
          {loading && <p className="px-3 py-2 text-[12px] text-[#98A2B3]">Searching…</p>}

          {results.map((club) => (
            <button
              key={`${club.wyscout_team_id}-${club.name}`}
              type="button"
              onClick={() => pick(club)}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-[#344054] hover:bg-[#F8FAFC]"
            >
              {club.logo ? (
                <img src={club.logo} alt="" className="w-6 h-6 rounded-full object-contain bg-white flex-none" referrerPolicy="no-referrer" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-[#EAF2FF] text-[#1866CC] text-[10px] font-bold flex items-center justify-center flex-none">{monogram(club.name)}</span>
              )}
              <span className="font-medium truncate">{club.name}</span>
              {club.country && <span className="ml-auto flex items-center gap-1 text-[11px] text-[#98A2B3]"><CountryFlag country={club.country} width={14} height={10} />{club.country}</span>}
            </button>
          ))}

          {/* Unlisted fallback — secondary, last row */}
          {query.trim().length >= 2 && !loading && (
            <button
              type="button"
              onClick={() => pick({ name: query.trim(), unlisted: true })}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-[13px] text-[#667085] hover:bg-[#F8FAFC] border-t border-[#F2F4F7]"
            >
              <span className="w-6 h-6 rounded-full bg-[#F2F4F7] text-[#667085] flex items-center justify-center flex-none"><Plus className="w-3.5 h-3.5" /></span>
              Add unlisted club “<span className="font-medium text-[#344054]">{query.trim()}</span>” (name only)
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ClubPicker;
